import { CollectionBadgeInfo, SectionSummary } from "@/services/hymnsApi";
import { getLanguageLabel, scrollToTop } from "@/utils";
import { ArrowRight, X } from "lucide-react";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const LANG_FLAG: Record<string, string> = {
  ht: "🇭🇹",
  fr: "🇫🇷",
  en: "🇺🇸",
  es: "🇪🇸",
};

interface SectionGroupSlidePanelProps {
  group: CollectionBadgeInfo;
  /** Full sections list from the API, used to resolve related codes */
  allSections: SectionSummary[];
  onClose: () => void;
}

export const SectionGroupSlidePanel: React.FC<SectionGroupSlidePanelProps> = ({
  group,
  allSections,
  onClose,
}) => {
  const navigate = useNavigate();

  /**
   * Resolve which individual sections belong to this group.
   *
   * Strategy (in order):
   * 1. Use the explicit `related_section_code` array if the API populated it.
   * 2. Prefix-match: find all non-group sections whose code starts with the
   *    group's code followed by "-" (e.g. group "CE" → "CE-CR", "CE-FR").
   * 3. Badge-match: find all non-group sections sharing the same badge acronym.
   */
  const relatedSections: SectionSummary[] = (() => {
    const nonGroups = allSections.filter((s) => !s.is_section_group);

    const sorted = (() => {
      // 1. Explicit list
      if (group.relatedSectionCodes.length > 0) {
        const explicit = nonGroups.filter((s) =>
          group.relatedSectionCodes.includes(s.section_code),
        );
        if (explicit.length > 0) return explicit;
      }

      // 2. Prefix-match  (group.code = "CE"  →  "CE-CR", "CE-FR")
      const prefix = group.code + "-";
      const byPrefix = nonGroups.filter((s) =>
        s.section_code.startsWith(prefix),
      );
      if (byPrefix.length > 0) return byPrefix;

      // 3. Badge-match  (group.badge = "CE"  →  all sections whose badge = "CE")
      const byBadge = nonGroups.filter((s) => {
        const sectionBadge =
          s.section_code.split("-")[0] ||
          s.section_code.substring(0, 2).toUpperCase();
        return sectionBadge === group.badge;
      });
      return byBadge;
    })();

    const unique = sorted.filter((s, index, self) => {
      const key = `${s.section_code}-${s.language || ""}`;
      return self.findIndex((item) => `${item.section_code}-${item.language || ""}` === key) === index;
    });

    return unique.sort((a, b) => {
      const orderA = a.order ?? Infinity;
      const orderB = b.order ?? Infinity;
      if (orderA !== orderB) return orderA - orderB;
      return (a.language || "").localeCompare(b.language || "");
    });
  })();

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Prevent body scroll while open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Extract all unique languages from the variants
  const allLanguages = React.useMemo(() => {
    const langs = new Set<string>();
    relatedSections.forEach((s) => {
      if (s.language && s.language.trim()) {
        langs.add(s.language.trim());
      }
    });
    return Array.from(langs);
  }, [relatedSections]);

  const totalHymnsAll = React.useMemo(() => {
    return relatedSections.reduce((sum, s) => sum + (s.total_hymns || 0), 0);
  }, [relatedSections]);

  const handleSelect = (sectionCode: string, lang?: string) => {
    scrollToTop();
    const query = lang ? `?lang=${encodeURIComponent(lang)}` : "";
    navigate(`/chants-desperance/${encodeURIComponent(sectionCode)}${query}`);
    onClose();
  };

  const handleSelectAllLanguages = () => {
    scrollToTop();
    const langQuery =
      allLanguages.length > 0
        ? `?lang=${encodeURIComponent(allLanguages.join(","))}`
        : "";
    const targetCode = group.code || group.badge || "CE";
    navigate(
      `/chants-desperance/${encodeURIComponent(targetCode)}${langQuery}`,
    );
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-slate-950/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel — bottom sheet on mobile, right side panel on md+ */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Choisir une variante de ${group.name}`}
        className={[
          "fixed z-50",
          // Mobile: bottom sheet
          "inset-x-0 bottom-0 rounded-t-3xl",
          // Desktop: right panel
          "md:inset-x-auto md:inset-y-0 md:right-0 md:w-96 md:rounded-l-3xl md:rounded-tr-none",
          "bg-slate-900 border border-slate-700/60 shadow-2xl",
          "flex flex-col",
          "animate-slide-in-panel",
        ].join(" ")}
      >
        {/* Header */}
        <div className="flex items-start justify-between mt-20 p-5 border-b border-slate-700/60">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-sky-400 mb-1">
              Choisir une variante
            </p>
            <h2 className="text-lg font-extrabold text-white leading-snug">
              {group.name}
            </h2>
            <p className="text-slate-400 text-sm mt-0.5">
              {relatedSections.length} variante
              {relatedSections.length !== 1 ? "s" : ""} disponible
              {relatedSections.length !== 1 ? "s" : ""}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Fermer"
            className="flex-shrink-0 mt-0.5 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Options list */}
        <ul className="flex-1 overflow-y-auto p-4 space-y-2 scrollbar-tiny">
          {relatedSections.map((s, idx) => {
            const flag = LANG_FLAG[s.language ?? ""] ?? "🌐";
            const langLabel = getLanguageLabel(s.language) || s.language || "";
            const cleanSectionName = (s.section || "")
              .replace(/\s*\([^)]*\)\s*$/, "")
              .trim();
            const titleWithLanguage =
              langLabel &&
              !cleanSectionName.toLowerCase().includes(langLabel.toLowerCase())
                ? `${cleanSectionName} (${langLabel})`
                : s.section || cleanSectionName;

            const itemKey = `${s.section_code}-${s.language || idx}-${idx}`;

            return (
              <li key={itemKey}>
                <button
                  onClick={() => handleSelect(s.section_code, s.language)}
                  className="group w-full flex items-center justify-between p-4 rounded-2xl bg-slate-800/70 hover:bg-slate-800 border border-slate-700/50 hover:border-sky-400/50 transition-all duration-200"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-2xl leading-none flex-shrink-0">
                      {flag}
                    </span>
                    <div className="min-w-0 text-left">
                      <p className="font-bold text-white text-sm leading-snug line-clamp-2 group-hover:text-sky-100 transition-colors">
                        {titleWithLanguage}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-700 text-sky-300 border border-slate-600">
                          {langLabel}
                        </span>
                        {s.total_hymns > 0 && (
                          <span className="text-xs text-slate-400">
                            {s.total_hymns} cantiques
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-sky-300 group-hover:translate-x-1 transition-all duration-200 flex-shrink-0 ml-2" />
                </button>
              </li>
            );
          })}

          {/* Button: Tous les langues (All languages combined) as the last button */}
          {relatedSections.length > 1 && (
            <li>
              <button
                onClick={handleSelectAllLanguages}
                className="group w-full flex items-center justify-between p-4 rounded-2xl bg-slate-800/70 hover:bg-slate-800 border border-slate-700/50 hover:border-sky-400/50 transition-all duration-200 cursor-pointer text-left"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-2xl leading-none flex-shrink-0">
                    🌐
                  </span>
                  <div className="min-w-0 text-left">
                    <p className="font-bold text-white text-sm leading-snug line-clamp-2 group-hover:text-sky-100 transition-colors">
                      Mélangés - Tous les langues
                    </p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-700 text-sky-300 border border-slate-600">
                        {allLanguages
                          .map((l) => getLanguageLabel(l))
                          .join(" & ") || "Toutes"}
                      </span>
                      {totalHymnsAll > 0 && (
                        <span className="text-xs text-slate-400">
                          {totalHymnsAll} cantiques
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-sky-300 group-hover:translate-x-1 transition-all duration-200 flex-shrink-0 ml-2" />
              </button>
            </li>
          )}

          {relatedSections.length === 0 && (
            <li className="text-center py-8 text-slate-500 text-sm">
              Aucune variante trouvée.
            </li>
          )}
        </ul>

        {/* Safe area spacer on mobile */}
        <div className="h-4 md:hidden" />
      </div>
    </>
  );
};
